import { useEffect, useMemo, useState } from 'react'
import { Download, Pencil, Plus, Send, Trash2, Upload } from 'lucide-react'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import Drawer from '../../components/Drawer'
import ExcelImportModal from '../../components/ExcelImportModal'
import FilterBar from '../../components/FilterBar'
import PromptDialog from '../../components/PromptDialog'
import RowActions from '../../components/RowActions'
import { FormBody } from '../../components/Form'
import { excel as EXCEL } from '../../components/strings'
import { useToast } from '../../components/toastContext'
import { phishingSettingsStrings as S } from './strings'
import { serverText } from '../../lib/i18n'

/**
 * One mailbox list — the sending profiles (SMTP), or the mailbox the reports are read from
 * (IMAP).
 *
 * Both tabs are the same transaction: a server-paged table, a form in a sheet, a delete, and
 * a TEST that talks to the mail server with the form as it stands rather than as it is
 * stored. The two differ only in their fields, so the fields are passed in as a render prop
 * and everything around them lives here once.
 *
 * TESTING NEVER SAVES. It posts what is typed, so a profile can be proven before it is
 * written — and a stored password the form does not carry is filled in server-side.
 *
 * @param {(context: {values: object, set: (key: string) => (value: any) => void, errors: object}) => import('react').ReactNode} children
 * @param {string} [stateKey]  WHICH mailbox list this is — the name its position is
 *   remembered under. The sending profiles and the read mailbox share this component, so
 *   without it they would share one position too; omit it and nothing is remembered.
 * @param {{label: string, icon?: import('react').ElementType, prompt?: {title: string, label: string, placeholder?: string}, run: Function}} [test]
 * @param {{onExport: Function, onImport: Function, note: string}} [transfer]
 */
export default function MailProfilesPanel({
  columns,
  labels,
  blank,
  icon,
  size = 'lg',
  stateKey,
  list,
  save,
  remove,
  validate,
  test,
  transfer,
  children,
}) {
  const toast = useToast()
  const TestIcon = test?.icon ?? Send
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  /* WHERE THIS TAB WAS LEFT. The panel unmounts on every tab switch, so the search, the
     page and the page size were all discarded by moving to another tab and back — page 1
     of an unsearched list every time. Remembered under this list's own name, since the
     sending profiles and the read mailbox are two lists sharing one component. */
  const [query, setQuery] = useRemembered(stateKey && `${stateKey}:query`, '')
  const [page, setPage] = useRemembered(stateKey && `${stateKey}:page`, 1)
  const [pageSize, setPageSize] = useRemembered(stateKey && `${stateKey}:pageSize`, 25)
  const [reloadKey, setReloadKey] = useState(0)
  const [editing, setEditing] = useState(null)
  const [values, setValues] = useState(blank)
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [asking, setAsking] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [importing, setImporting] = useState(false)

  /* A new search is a new list, so it starts at its first page — on a CHANGE only, or the
     remembered page would be thrown away the moment it was restored. The table cannot do
     this itself: the list is SERVER-paged, so the page belongs to this panel. */
  useOnChange(() => setPage(1), [query])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    list({ page, pageSize, query })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setTotal(response.total)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
    return () => {
      cancelled = true
    }
  }, [list, page, pageSize, query, reloadKey, toast])

  const open = (row) => {
    /* The password is never listed, so the field opens empty on a stored profile and an
       untouched form keeps what the server holds. */
    setValues(row ? { ...blank, ...row, password: '' } : blank)
    setErrors({})
    setEditing(row ?? {})
  }

  const close = () => {
    setEditing(null)
    setAsking(false)
  }

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const check = () => {
    const found = validate?.(values) ?? {}
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!check()) return
    setSaving(true)
    const outcome = await save(values)
    setSaving(false)
    if (!outcome.ok) {
      toast.error(outcome.message || S.actionFailed)
      return
    }
    toast.success(serverText(outcome.message) || S.saved)
    close()
    setReloadKey((key) => key + 1)
  }

  const runTest = async (target) => {
    setTesting(true)
    const outcome = await test.run(values, target)
    setTesting(false)
    setAsking(false)
    if (outcome.ok) toast.success(serverText(outcome.message) || S.saved)
    // The mail server's own refusal is the answer a test gives — show it as it came.
    else toast.error(outcome.message || S.actionFailed)
  }

  const startTest = () => {
    if (!check()) return
    if (test.prompt) setAsking(true)
    else runTest()
  }

  const confirmDelete = async () => {
    const row = pendingDelete
    setPendingDelete(null)
    const outcome = await remove(row.id)
    /* The SMTP delete REFUSES while a campaign still sends from the profile, and says so. */
    if (!outcome.ok) {
      toast.error(outcome.message || S.actionFailed)
      return
    }
    toast.success(serverText(outcome.message) || S.deleted)
    setReloadKey((key) => key + 1)
  }

  const runImport = async (file) => {
    setImporting(false)
    const outcome = await transfer.onImport(file)
    if (!outcome.ok) {
      toast.error(outcome.message || S.actionFailed)
      return
    }
    toast.success(serverText(outcome.message) || labels.imported)
    setReloadKey((key) => key + 1)
  }

  const runExport = async () => {
    const outcome = await transfer.onExport()
    if (!outcome.ok) toast.error(outcome.message || S.actionFailed)
  }

  const allColumns = useMemo(
    () => [
      ...columns,
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <RowActions
            actions={[
              { id: 'edit', icon: Pencil, label: S.edit, onClick: () => open(row) },
              {
                id: 'delete',
                icon: Trash2,
                label: S.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    // `open` closes over `blank`, which is stable per tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns],
  )

  return (
    <>
      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: labels.searchPlaceholder }}
        actions={
          <>
            {transfer && (
              <>
                <Button variant="soft" onClick={() => setImporting(true)}>
                  <Upload size={16} strokeWidth={1.75} />
                  {S.import}
                </Button>
                <Button variant="soft" onClick={runExport}>
                  <Download size={16} strokeWidth={1.75} />
                  {S.export}
                </Button>
              </>
            )}
            <Button onClick={() => open(null)}>
              <Plus size={16} strokeWidth={2} />
              {labels.add}
            </Button>
          </>
        }
      />

      <DataTable
        columns={allColumns}
        rows={rows}
        loading={loading}
        query={query}
        getRowId={(row) => row.id}
        emptyMessage={labels.empty}
        server={{
          page,
          pageSize,
          total,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
          },
        }}
      />

      <Drawer
        dialog
        size={size}
        icon={icon}
        open={!!editing}
        title={editing?.id ? labels.edit : labels.add}
        subtitle={labels.subtitle}
        onClose={close}
        footer={
          <>
            <Button type="submit" form="mail-profile-form" disabled={saving || testing}>
              {S.save}
            </Button>
            {test && (
              <Button variant="soft" onClick={startTest} disabled={saving || testing}>
                <TestIcon size={16} strokeWidth={1.75} />
                {test.label}
              </Button>
            )}
            <Button variant="ghost" onClick={close}>
              {S.close}
            </Button>
          </>
        }
      >
        <form id="mail-profile-form" onSubmit={submit}>
          <FormBody>{children({ values, set, errors })}</FormBody>
        </form>
      </Drawer>

      {/* The test message needs somewhere to land, and that is not a profile field. */}
      {test?.prompt && (
        <PromptDialog
          open={asking}
          icon={TestIcon}
          title={test.prompt.title}
          subtitle={labels.subtitle}
          label={test.prompt.label}
          placeholder={test.prompt.placeholder}
          type="email"
          dir="ltr"
          confirmLabel={test.label}
          cancelLabel={S.close}
          busy={testing}
          onSubmit={runTest}
          onClose={() => setAsking(false)}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        message={labels.deleteConfirm}
        confirmLabel={S.delete}
        cancelLabel={S.close}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {transfer && (
        <ExcelImportModal
          open={importing}
          title={S.import}
          accept=".xls,.xlsx"
          strings={{ intro: EXCEL.intro, notes: transfer.note }}
          onClose={() => setImporting(false)}
          onSubmit={runImport}
        />
      )}
    </>
  )
}
